import React from 'react';
import PropTypes from 'prop-types';
import { Form, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';

/**
 * ReactFinalFormBridge
 *
 * A bridge component that allows React Final Form components to work within a Redux Form parent.
 * This is a temporary solution during migration from Redux Form to React Final Form.
 *
 * This component wraps child components (that use React Final Form) with a <Form> component,
 * allowing them to work independently even when the parent form is still using Redux Form.
 *
 * @param {Object} initialValues - Initial values for the form
 * @param {Function} onFieldChange - Callback when field values change
 * @param {Function} onValidationChange - Callback when validation state changes (errors, invalid)
 * @param {Function} formRef - Ref callback to expose the form API to parent
 * @param {ReactNode} children - Child components that use React Final Form
 */
const ReactFinalFormBridge = ({
  initialValues,
  onFieldChange,
  onValidationChange,
  formRef,
  children,
}) => {
  const prevValuesRef = React.useRef();
  const prevErrorsRef = React.useRef();

  // Handle value changes - notify parent when form values change
  // Use FormSpy's onChange which only fires when values actually change
  const handleValuesChange = React.useCallback((formState) => {
    if (!onFieldChange) return;

    const values = formState.values;

    // Deep equality check using JSON.stringify to avoid infinite loops
    const prevSerialized = JSON.stringify(prevValuesRef.current);
    const currentSerialized = JSON.stringify(values);

    if (prevSerialized !== currentSerialized) {
      prevValuesRef.current = values;
      onFieldChange(values);
    }
  }, [onFieldChange]);

  // Handle validation changes - notify parent when validation state changes
  const handleValidationChange = React.useCallback((formState) => {
    if (!onValidationChange) return;

    const { errors, invalid, hasValidationErrors } = formState;

    // Deep equality check to avoid infinite loops
    const prevSerialized = JSON.stringify(prevErrorsRef.current);
    const currentSerialized = JSON.stringify(errors);

    if (prevSerialized !== currentSerialized) {
      prevErrorsRef.current = errors;
      onValidationChange({ errors, invalid, hasValidationErrors });
    }
  }, [onValidationChange]);

  return (
    <Form
      onSubmit={() => {}} // No-op submit since parent form handles submission
      initialValues={initialValues}
      // Allow reinitialization when values change, but keep user changes once they start editing
      keepDirtyOnReinitialize
      // This function determines if initialValues have "truly" changed
      // Returns false when they're different, triggering reinitialization
      initialValuesEqual={(a, b) => {
        // Null/undefined guard
        if (!a && !b) return true;
        if (!a || !b) return false;
        // Deep equality check - reinitialize only when values truly differ
        return JSON.stringify(a) === JSON.stringify(b);
      }}
      mutators={{
        ...arrayMutators, // Required for FieldArray components
        // Mutator to touch all fields (trigger validation display)
        // eslint-disable-next-line no-param-reassign
        touchAllFields: (args, state, utils) => {
          Object.keys(state.fields).forEach((fieldName) => {
            utils.changeValue(state, fieldName, (value) => value);
            // eslint-disable-next-line no-param-reassign
            state.fields[fieldName].touched = true;
          });
        },
      }}
    >
      {({ form }) => {
        // Expose form API to parent via ref callback
        if (formRef) {
          formRef(form);
        }

        return (
          <>
            {onFieldChange && (
              <FormSpy subscription={{ values: true }} onChange={handleValuesChange} />
            )}
            {onValidationChange && (
              <FormSpy
                subscription={{ errors: true, invalid: true, hasValidationErrors: true }}
                onChange={handleValidationChange}
              />
            )}
            <div>{children}</div>
          </>
        );
      }}
    </Form>
  );
};

ReactFinalFormBridge.propTypes = {
  initialValues: PropTypes.object,
  onFieldChange: PropTypes.func,
  onValidationChange: PropTypes.func,
  formRef: PropTypes.func,
  children: PropTypes.node.isRequired,
};

ReactFinalFormBridge.defaultProps = {
  initialValues: {},
  onFieldChange: null,
  onValidationChange: null,
  formRef: null,
};

export default ReactFinalFormBridge;
